import { FC, useRef, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import { EmissionSourceDistributionResponse, formatNumber } from './types';
import ChartErrorState from './ChartErrorState';
import { sourceColors, chartChrome } from '../../utils/chartColors';
import { formatDecimal } from '../../utils/localeFormat';
import './EmissionSourceDistributionChart.scss';

interface EmissionSourceDistributionChartProps {
  data: EmissionSourceDistributionResponse | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

const EmissionSourceDistributionChart: FC<EmissionSourceDistributionChartProps> = ({ data, isLoading, error, onRetry }) => {
  const { t, i18n } = useTranslation();
  // optional: formatters fall back to the i18next fallbackLng when absent,
  // which must not become a crash if useTranslation yields no i18n
  const lng = i18n?.language;
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);

  const chartCallbackRef = useCallback((node: HTMLDivElement | null) => {
    setChartNode(node);
  }, []);

  useEffect(() => {
    if (!chartNode || !data || data.sources.length === 0) {
      return;
    }

    if (!chartInstance.current || chartInstance.current.getDom() !== chartNode) {
      chartInstance.current?.dispose();
      chartInstance.current = echarts.init(chartNode);
    }

    const sources = sourceColors();
    const chrome = chartChrome();

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const source = data.sources.find((s) => s.emissionSource === params.name);
          // The kg half already followed the language; the percentage was
          // interpolated raw, so one sentence held both conventions.
          return `${params.name}: ${formatNumber(params.value)} kg (${formatDecimal(
            source?.percentage ?? 0,
            1,
            lng
          )}%)`;
        },
      },
      legend: {
        orient: 'horizontal',
        bottom: '0%',
        left: 'center',
        textStyle: { color: chrome.text, fontSize: 13 },
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 4,
            borderColor: chrome.surface,
            borderWidth: 2,
          },
          label: {
            show: true,
            formatter: (params: any) => {
              const source = data.sources.find((s) => s.emissionSource === params.name);
              return `${source?.percentage ?? 0}%`;
            },
            color: chrome.text,
            fontSize: 12,
          },
          data: data.sources.map((s) => ({
            name: s.emissionSource,
            value: s.wtwKg,
            itemStyle: { color: sources[s.emissionSource] || chrome.muted },
          })),
        },
      ],
    };

    chartInstance.current.setOption(option, true);
  }, [data, chartNode, lng]);

  // Handle resize & cleanup
  useEffect(() => {
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (error && !isLoading) {
    return (
      <div className="emission-source-chart">
        <h3 className="emission-source-chart__title">{t('cpi.emissionSourceDistribution')}</h3>
        <ChartErrorState onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="emission-source-chart emission-source-chart--loading">
        <h3 className="emission-source-chart__title">{t('cpi.emissionSourceDistribution')}</h3>
        <div className="emission-source-chart__skeleton" />
      </div>
    );
  }

  if (!data || data.sources.length === 0) {
    return (
      <div className="emission-source-chart">
        <h3 className="emission-source-chart__title">{t('cpi.emissionSourceDistribution')}</h3>
        <p className="chart-no-data">{t('cpi.noDataAvailableForChart')}</p>
      </div>
    );
  }

  return (
    <div className="emission-source-chart">
      <h3 className="emission-source-chart__title">{t('cpi.emissionSourceDistribution')}</h3>
      <div
        ref={chartCallbackRef}
        style={{ height: '320px', width: '100%' }}
      />
    </div>
  );
};

export default EmissionSourceDistributionChart;
